// legacyGaugeSlider — `LegacyGaugeSlider` component の値変換 pure helper.
//
// 元は `penWidthSlider.ts` の `xToPenWidth` / `penWidthToX` を valueMin / valueMax /
// step を任意化して一般化したもの. 筆圧設定 UI (= 0..1, step 0.05) でも再利用できる
// ようにした (consult 2026-05-09).
//
// pure function として切り出してある理由:
//   - vitest が node 環境で動く (jsdom 不在) ため component 自体を mount せずに
//     値変換ロジックだけ単体テストできる.
//   - 既存 `penWidthSlider.test.ts` と同流儀.

/**
 * Canvas (or HTML 要素) 上の x 座標 → valueMin..valueMax の値.
 *
 * @param xCanvas pointer の x 座標 (バー左端を 0 とした px).
 * @param barWidth バーの描画幅 (px). xCanvas / barWidth で 0..1 に正規化.
 * @param valueMin 値の最小 (例: 0).
 * @param valueMax 値の最大 (例: 60 や 1).
 * @param step 値の刻み (default 1). raw 値を `Math.round(raw / step) * step` で snap.
 * @returns valueMin..valueMax にクランプ + step snap 済の値. 範囲外は端で clamp.
 */
export const xToValue = (
  xCanvas: number,
  barWidth: number,
  valueMin: number,
  valueMax: number,
  step = 1,
): number => {
  if (barWidth <= 0) return valueMin;
  if (valueMax <= valueMin) return valueMin;
  const ratio = Math.max(0, Math.min(1, xCanvas / barWidth));
  const raw = valueMin + ratio * (valueMax - valueMin);
  // step snap. step <= 0 は防御的に raw のまま (= no snap).
  const snapped = step > 0 ? Math.round(raw / step) * step : raw;
  // clamp (snap で valueMax を超えうるので最後にもう一度 clamp).
  const clamped = Math.max(valueMin, Math.min(valueMax, snapped));
  // 浮動小数の累積誤差対策: step が 0.05 のとき 0.30000000000000004 を 0.3 に丸める.
  // step <= 0 の防御パスでは丸めない (= raw をそのまま返す契約を維持).
  if (step <= 0) return clamped;
  // step の小数桁数を求めて round. step=0.05 → 2 桁、step=1 → 0 桁.
  // toString で "0.05" / "1" / "1e-7" などの表現を扱える.
  const decimals = countDecimals(step);
  if (decimals === 0) return clamped;
  const factor = 10 ** decimals;
  const rounded = Math.round(clamped * factor) / factor;
  // float decimal round が valueMin/valueMax を超えうる (例: clamped=0.99, factor=10
  // → rounded=1.0 で valueMax=0.99 を超える) のでもう一度 clamp.
  return Math.max(valueMin, Math.min(valueMax, rounded));
};

/**
 * 与えられた値 `v` がバー上のどの x 座標に対応するか. 描画 (ゲージ右端) で使う.
 *
 * @returns 0..barWidth の x 座標 (px).
 */
export const valueToX = (
  v: number,
  barWidth: number,
  valueMin: number,
  valueMax: number,
): number => {
  if (valueMax <= valueMin) return 0;
  const clamped = Math.max(valueMin, Math.min(valueMax, v));
  return ((clamped - valueMin) / (valueMax - valueMin)) * barWidth;
};

/**
 * step の小数桁数を求める. `0.05` → 2, `1` → 0, `0.1` → 1.
 *
 * 浮動小数の文字列表現 (例: `1e-7`) も扱える防御的実装.
 */
const countDecimals = (step: number): number => {
  if (!Number.isFinite(step) || step === 0) return 0;
  const s = Math.abs(step).toString();
  if (s.includes('e')) {
    // 1e-7 形式. 指数部の絶対値がそのまま小数桁数 (mantissa が 1 のとき).
    const [, expStr] = s.split('e');
    const exp = expStr ? Number.parseInt(expStr, 10) : 0;
    if (exp < 0) return Math.abs(exp);
    return 0;
  }
  const dotIdx = s.indexOf('.');
  if (dotIdx < 0) return 0;
  return s.length - dotIdx - 1;
};
